import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../App";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState({ tenant: '', role: '', status: '' });
  const { user: currentUser } = useAuth();

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    if (currentUser?.role === 'super_admin') {
      try {
        const response = await api.get('/tenants');
        setTenants(response.data);
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, [currentUser]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleCreateUser = async (data) => {
    try {
      await api.post('/users', data);
      toast.success('Utilizador criado com sucesso');
      fetchUsers();
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await api.put(`/users/${userId}`, data);
      toast.success('Utilizador atualizado com sucesso');
      fetchUsers();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar utilizador');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem a certeza que deseja desativar este utilizador?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilizador desativado com sucesso');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desativar utilizador');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await api.put(`/users/${userId}/activate`);
      toast.success('Utilizador reativado com sucesso');
      fetchUsers();
    } catch (error) {
      console.error('Error reactivating user:', error);
      toast.error('Erro ao reativar utilizador');
    }
  };

  const handlePermanentDelete = async (userId, userEmail) => {
    const confirmText = prompt(`Esta ação é IRREVERSÍVEL!\n\nPara confirmar a eliminação permanente, digite o email do utilizador:\n${userEmail}`);
    if (confirmText !== userEmail) {
      if (confirmText !== null) {
        toast.error('Email não corresponde. Eliminação cancelada.');
      }
      return;
    }
    try {
      await api.delete(`/users/${userId}/permanent`);
      toast.success('Utilizador eliminado permanentemente');
      fetchUsers();
    } catch (error) {
      console.error('Error permanently deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar utilizador');
    }
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    tenant_admin: 'Admin Tenant',
    compliance_officer: 'Compliance Officer',
    hr_manager: 'Gestor RH',
    colaborador: 'Colaborador',
    user: 'Utilizador',
    viewer: 'Visualizador'
  };

  const filteredUsers = users.filter(u => {
    if (filter.tenant && u.tenant_id !== filter.tenant) return false;
    if (filter.role && u.role !== filter.role) return false;
    if (filter.status === 'active' && u.is_active === false) return false;
    if (filter.status === 'inactive' && u.is_active !== false) return false;
    return true;
  });

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    return tenant?.name || tenantId || 'N/A';
  };

  const canCreateUser = currentUser?.role === 'super_admin' || currentUser?.role === 'tenant_admin';

  return (
    <div data-testid="users-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Utilizadores</h1>
          <p className="text-muted-foreground">Visualização, edição e gestão de permissões</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Total: {filteredUsers.length} utilizadores</span>
          {canCreateUser && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
              data-testid="create-user-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Utilizador
            </button>
          )}
        </div>
      </div>

      {(currentUser?.role === 'super_admin' || currentUser?.role === 'tenant_admin') && (
        <div className="flex flex-wrap gap-4">
          {currentUser?.role === 'super_admin' && (
            <select value={filter.tenant} onChange={(e) => setFilter({ ...filter, tenant: e.target.value })} className="px-3 py-2 rounded-lg border border-input bg-background text-sm" data-testid="filter-tenant">
              <option value="">Todos os Tenants</option>
              {tenants.map(t => (<option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>))}
            </select>
          )}
          <select value={filter.role} onChange={(e) => setFilter({ ...filter, role: e.target.value })} className="px-3 py-2 rounded-lg border border-input bg-background text-sm" data-testid="filter-role">
            <option value="">Todas as Roles</option>
            {currentUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
            <option value="tenant_admin">Admin Tenant</option>
            <option value="compliance_officer">Compliance Officer</option>
            <option value="hr_manager">Gestor RH</option>
            <option value="colaborador">Colaborador (Portal)</option>
            <option value="user">Utilizador</option>
            <option value="viewer">Visualizador</option>
          </select>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="px-3 py-2 rounded-lg border border-input bg-background text-sm" data-testid="filter-status">
            <option value="">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <p className="text-muted-foreground">Nenhum utilizador encontrado</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Utilizador</th>
                <th>Email</th>
                {currentUser?.role === 'super_admin' && <th>Tenant</th>}
                <th>Role</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                        {u.picture ? (<img src={u.picture} alt={u.name} className="w-full h-full object-cover" />) : (<span className="text-sm font-medium">{u.name?.charAt(0)?.toUpperCase()}</span>)}
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        {u.user_id === currentUser?.user_id && (<span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary">Você</span>)}
                        {u.employee_id && (<span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 ml-1">Portal</span>)}
                      </div>
                    </div>
                  </td>
                  <td className="text-muted-foreground">{u.email}</td>
                  {currentUser?.role === 'super_admin' && (<td className="text-muted-foreground text-sm">{getTenantName(u.tenant_id)}</td>)}
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      u.role === 'super_admin' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' : 
                      u.role === 'tenant_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30' : 
                      u.role === 'compliance_officer' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' : 
                      u.role === 'hr_manager' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' :
                      u.role === 'colaborador' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-red-100 text-red-700 dark:bg-red-900/30'}`}>
                      {u.is_active !== false ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {(currentUser?.role === 'super_admin' || (currentUser?.role === 'tenant_admin' && u.tenant_id === currentUser?.tenant_id)) && (
                        <>
                          <button onClick={() => handleEditUser(u)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Editar" data-testid={`edit-user-${u.user_id}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          {u.user_id !== currentUser?.user_id && u.role !== 'super_admin' && (
                            u.is_active !== false ? (
                              <button onClick={() => handleDeleteUser(u.user_id)} className="p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 transition-colors" title="Desativar" data-testid={`deactivate-user-${u.user_id}`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                              </button>
                            ) : (
                              <>
                                <button onClick={() => handleReactivateUser(u.user_id)} className="p-1.5 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-500 transition-colors" title="Reativar" data-testid={`reactivate-user-${u.user_id}`}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                </button>
                                {currentUser?.role === 'super_admin' && (
                                  <button onClick={() => handlePermanentDelete(u.user_id, u.email)} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 transition-colors" title="Eliminar Permanentemente" data-testid={`permanent-delete-user-${u.user_id}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                )}
                              </>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEditModal && selectedUser && (
        <UserEditModal user={selectedUser} tenants={tenants} currentUser={currentUser} onClose={() => setShowEditModal(false)} onSave={(data) => handleUpdateUser(selectedUser.user_id, data)} />
      )}

      {showCreateModal && (
        <UserCreateModal tenants={tenants} currentUser={currentUser} onClose={() => setShowCreateModal(false)} onSave={handleCreateUser} />
      )}
    </div>
  );
};

const UserEditModal = ({ user, tenants, currentUser, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user.name || '', email: user.email || '', role: user.role || 'user',
    tenant_id: user.tenant_id || '', is_active: user.is_active !== false,
    department: user.department || '', position: user.position || ''
  });
  const [saving, setSaving] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) { toast.error('A password deve ter pelo menos 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { toast.error('As passwords não coincidem'); return; }
    setSavingPassword(true);
    try {
      await api.put(`/users/${user.user_id}/password`, { new_password: newPassword });
      toast.success('Password alterada com sucesso');
      setNewPassword(''); setConfirmPassword(''); setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar password');
    } finally { setSavingPassword(false); }
  };

  const roleOptions = currentUser?.role === 'super_admin' 
    ? [{ value: 'super_admin', label: 'Super Admin' }, { value: 'tenant_admin', label: 'Admin Tenant' }, { value: 'compliance_officer', label: 'Compliance Officer' }, { value: 'hr_manager', label: 'Gestor RH' }, { value: 'colaborador', label: 'Colaborador (Portal)' }, { value: 'user', label: 'Utilizador' }, { value: 'viewer', label: 'Visualizador' }]
    : [{ value: 'tenant_admin', label: 'Admin Tenant' }, { value: 'compliance_officer', label: 'Compliance Officer' }, { value: 'hr_manager', label: 'Gestor RH' }, { value: 'colaborador', label: 'Colaborador (Portal)' }, { value: 'user', label: 'Utilizador' }, { value: 'viewer', label: 'Visualizador' }];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Editar Utilizador</h2>
          <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required data-testid="user-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required data-testid="user-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" data-testid="user-role-select">
                {roleOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
            </div>
            {currentUser?.role === 'super_admin' && (
              <div>
                <label className="block text-sm font-medium mb-1">Tenant</label>
                <select value={formData.tenant_id} onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" data-testid="user-tenant-select">
                  <option value="">Sem Tenant (Super Admin)</option>
                  {tenants.map(t => (<option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Departamento</label>
              <input type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Ex: Compliance" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cargo</label>
              <input type="text" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Ex: Diretor" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded border-input" data-testid="user-active-checkbox" />
            <label htmlFor="is_active" className="text-sm font-medium">Utilizador Ativo</label>
          </div>

          {currentUser?.role === 'super_admin' && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  Alterar Password
                </h3>
                <button type="button" onClick={() => setShowPasswordSection(!showPasswordSection)} className="text-sm text-primary hover:underline">
                  {showPasswordSection ? 'Cancelar' : 'Definir Nova Password'}
                </button>
              </div>
              {showPasswordSection && (
                <div className="space-y-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">⚠️ Ao definir uma nova password, o utilizador será notificado.</p>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nova Password *</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Mínimo 6 caracteres" data-testid="new-password-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Confirmar Password *</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Repetir password" data-testid="confirm-password-input" />
                  </div>
                  <button type="button" onClick={handlePasswordChange} disabled={savingPassword || !newPassword || !confirmPassword} className="w-full px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="change-password-btn">
                    {savingPassword ? 'A alterar...' : 'Alterar Password'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Cancelar</button>
            <button type="submit" disabled={saving || !formData.name || !formData.email} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="save-user-btn">
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserCreateModal = ({ tenants, currentUser, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    tenant_id: currentUser?.tenant_id || '',
    department: '',
    position: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('A password deve ter pelo menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    const { confirmPassword, ...dataToSend } = formData;
    await onSave(dataToSend);
    setSaving(false);
  };

  const roleOptions = currentUser?.role === 'super_admin' 
    ? [
        { value: 'super_admin', label: 'Super Admin', desc: 'Acesso total a todos os tenants' },
        { value: 'tenant_admin', label: 'Admin Tenant', desc: 'Administrador do tenant' },
        { value: 'compliance_officer', label: 'Compliance Officer', desc: 'Gestão de compliance e conflitos' },
        { value: 'hr_manager', label: 'Gestor RH', desc: 'Gestão de recursos humanos' },
        { value: 'colaborador', label: 'Colaborador (Portal)', desc: 'Apenas acesso ao portal self-service' },
        { value: 'user', label: 'Utilizador', desc: 'Acesso básico' },
        { value: 'viewer', label: 'Visualizador', desc: 'Apenas leitura' }
      ]
    : [
        { value: 'tenant_admin', label: 'Admin Tenant', desc: 'Administrador do tenant' },
        { value: 'compliance_officer', label: 'Compliance Officer', desc: 'Gestão de compliance e conflitos' },
        { value: 'hr_manager', label: 'Gestor RH', desc: 'Gestão de recursos humanos' },
        { value: 'colaborador', label: 'Colaborador (Portal)', desc: 'Apenas acesso ao portal self-service' },
        { value: 'user', label: 'Utilizador', desc: 'Acesso básico' },
        { value: 'viewer', label: 'Visualizador', desc: 'Apenas leitura' }
      ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Novo Utilizador</h2>
          <p className="text-sm text-muted-foreground mt-1">Criar uma nova conta de utilizador</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input 
              type="text" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
              required 
              placeholder="Nome completo"
              data-testid="create-user-name" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input 
              type="email" 
              value={formData.email} 
              onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
              className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
              required 
              placeholder="email@empresa.pt"
              data-testid="create-user-email" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Password *</label>
              <input 
                type="password" 
                value={formData.password} 
                onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                required 
                placeholder="Mínimo 6 caracteres"
                data-testid="create-user-password" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirmar Password *</label>
              <input 
                type="password" 
                value={formData.confirmPassword} 
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} 
                className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                required 
                placeholder="Repetir password"
                data-testid="create-user-confirm-password" 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select 
                value={formData.role} 
                onChange={(e) => setFormData({ ...formData, role: e.target.value })} 
                className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                data-testid="create-user-role"
              >
                {roleOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {roleOptions.find(r => r.value === formData.role)?.desc}
              </p>
            </div>
            {currentUser?.role === 'super_admin' && (
              <div>
                <label className="block text-sm font-medium mb-1">Tenant</label>
                <select 
                  value={formData.tenant_id} 
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                  data-testid="create-user-tenant"
                >
                  <option value="">Sem Tenant (Super Admin)</option>
                  {tenants.map(t => (
                    <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {formData.role === 'colaborador' && (
            <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-700">
              <p className="text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Este utilizador terá acesso apenas ao Portal Self-Service.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Departamento</label>
              <input 
                type="text" 
                value={formData.department} 
                onChange={(e) => setFormData({ ...formData, department: e.target.value })} 
                className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                placeholder="Ex: Compliance"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cargo</label>
              <input 
                type="text" 
                value={formData.position} 
                onChange={(e) => setFormData({ ...formData, position: e.target.value })} 
                className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                placeholder="Ex: Diretor"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={saving || !formData.name || !formData.email || !formData.password} 
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50" 
              data-testid="create-user-submit"
            >
              {saving ? 'A criar...' : 'Criar Utilizador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
